import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import {
  LogOut,
  RefreshCw,
  Phone,
  Mail,
  HandCoins,
  Inbox,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Loader2,
  Flame,
  BookOpen,
} from "lucide-react";
import { adminApi, tokenStorage } from "@/lib/api";
import Logo from "@/components/Logo";
import BrandBanner from "@/components/BrandBanner";

const STATUS_META = {
  registered: { label: "Registered", color: "bg-blue-400/15 text-blue-200 border-blue-400/30" },
  acknowledged: { label: "Acknowledged", color: "bg-cyan-400/15 text-cyan-200 border-cyan-400/30" },
  investigating: { label: "Investigating", color: "bg-yellow-300/15 text-yellow-200 border-yellow-300/30" },
  resolved: { label: "Resolved", color: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30" },
  closed: { label: "Closed", color: "bg-zinc-400/10 text-zinc-300 border-zinc-400/25" },
};

const ALL_STATUSES = ["registered", "acknowledged", "investigating", "resolved", "closed"];
const PAGE_SIZE = 25;

export default function AdminGrievances() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [updating, setUpdating] = useState(null);
  const hasToken = Boolean(tokenStorage.get());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await adminApi.get("/grievances", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, navigate]);

  useEffect(() => {
    if (hasToken) load();
  }, [load, hasToken]);

  if (!hasToken) return <Navigate to="/admin" replace />;

  const logout = async () => {
    try { await adminApi.post("/auth/logout"); } catch (_e) { /* noop */ }
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  const updateStatus = async (g, newStatus) => {
    setUpdating(g.id);
    try {
      const { data } = await adminApi.patch(`/grievances/${g.id}`, { status: newStatus });
      setItems((prev) => prev.map((it) => (it.id === g.id ? { ...it, ...data.grievance } : it)));
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not update status.");
    } finally {
      setUpdating(null);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="admin-grievances-page" className="min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/90 backdrop-blur-md border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[140px] flex items-center justify-between gap-4">
          <button
            data-testid="grv-back-home"
            onClick={() => navigate("/")}
            className="flex items-center gap-4 text-left"
          >
            <div className="leading-tight">
              <div className="font-display text-sm md:text-lg uppercase tracking-tight font-semibold whitespace-nowrap">
                SAHYOG FINANCE
                <br />
                <span className="text-[#D4AF37]">&amp; NOBLE INFINITY</span>
              </div>
              <div className="font-display text-[#D4AF37] text-[10px] md:text-xs tracking-[0.18em] uppercase mt-1">
                Asset Management Pvt. Ltd. · Admin Console
              </div>
            </div>
          </button>
          <div className="flex items-center gap-3">
            <button
              data-testid="grv-refresh"
              onClick={load}
              disabled={loading}
              className="text-white/80 hover:text-[#F0D788] inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/10 text-base font-semibold"
            >
              <RefreshCw size={20} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <a href="/admin/change-password" data-testid="admin-change-pw-link" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-[#F0D788] text-base font-semibold">Change password</a>
            <button
              data-testid="grv-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 text-sm border border-white/10"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        {/* Admin nav tabs */}
        <div className="flex flex-wrap items-center gap-3">
          <Link data-testid="nav-tab-leads" to="/admin/leads" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm">
            <Inbox size={14} /> Leads
          </Link>
          <Link data-testid="nav-tab-applications" to="/admin/applications" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm">
            <HandCoins size={14} /> Applications
          </Link>
          <span data-testid="nav-tab-grievances" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] border border-[#D4AF37] text-sm font-semibold">
            <AlertCircle size={14} /> Grievances ({total})
          </span>
          <Link data-testid="nav-tab-offers" to="/admin/offers" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm">
            <Flame size={14} /> Hot Offers
          </Link>
          <Link data-testid="nav-tab-blogs" to="/admin/blogs" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm">
            <BookOpen size={14} /> Blogs
          </Link>
        </div>

        {/* Status chips */}
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            data-testid="filter-status-all"
            onClick={() => { setStatusFilter("all"); setPage(0); }}
            className={`px-3 py-1.5 rounded-full text-xs border transition ${statusFilter === "all" ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37]" : "bg-white/[0.04] text-white/75 border-white/10 hover:border-[#D4AF37]/40"}`}
          >
            All ({total})
          </button>
          {ALL_STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`filter-status-${s}`}
              onClick={() => { setStatusFilter(s); setPage(0); }}
              className={`px-3 py-1.5 rounded-full text-xs border transition capitalize ${statusFilter === s ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37]" : "bg-white/[0.04] text-white/75 border-white/10 hover:border-[#D4AF37]/40"}`}
            >
              {STATUS_META[s].label}
            </button>
          ))}
        </div>

        {/* Table */}
        <section className="mt-6 rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="grv-table" className="w-full text-sm">
              <thead className="bg-white/[0.04] text-white/55 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left p-4">Ticket</th>
                  <th className="text-left p-4">Customer</th>
                  <th className="text-left p-4">Category</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Created</th>
                  <th className="text-left p-4">Detail</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="p-10 text-center text-white/70">Loading…</td></tr>}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="p-10 text-center text-white/70">No grievances yet.</td></tr>
                )}
                {!loading && items.map((g) => {
                  const M = STATUS_META[g.status] || STATUS_META.registered;
                  const isOpen = expanded === g.id;
                  return (
                    <Fragment key={g.id}>
                      <tr data-testid={`grv-row-${g.id}`} className="border-t border-white/5 hover:bg-white/[0.03]">
                        <td className="p-4 align-top">
                          <div className="font-mono text-[#F0D788] text-sm">{g.ticket_no}</div>
                          <div className="text-[10px] text-white/35 mt-1">L{g.escalation_level || 1}</div>
                        </td>
                        <td className="p-4 align-top">
                          <div className="font-display text-base">{g.name}</div>
                          <a href={`tel:${g.phone}`} className="flex items-center gap-1.5 text-white/70 text-xs mt-1 hover:text-[#F0D788]">
                            <Phone size={11} className="text-[#D4AF37]" /> {g.phone}
                          </a>
                          {g.email && (
                            <a href={`mailto:${g.email}`} className="flex items-center gap-1.5 text-white/55 text-xs mt-0.5 hover:text-[#F0D788]">
                              <Mail size={10} className="text-[#D4AF37]" /> {g.email}
                            </a>
                          )}
                        </td>
                        <td className="p-4 align-top text-white/80">{g.category}</td>
                        <td className="p-4 align-top">
                          <select
                            data-testid={`grv-status-${g.id}`}
                            value={g.status}
                            disabled={updating === g.id}
                            onChange={(e) => updateStatus(g, e.target.value)}
                            className={`px-2.5 py-1 rounded-full text-[11px] border uppercase tracking-wider bg-[#0A1F44] ${M.color} disabled:opacity-50 focus:outline-none focus:border-[#D4AF37]`}
                          >
                            {ALL_STATUSES.map((s) => (
                              <option key={s} value={s} className="bg-[#0A1F44] text-white">
                                {STATUS_META[s].label}
                              </option>
                            ))}
                          </select>
                          {updating === g.id && <Loader2 size={11} className="inline ml-2 animate-spin text-[#F0D788]" />}
                        </td>
                        <td className="p-4 align-top text-white/65 whitespace-nowrap text-xs">
                          {new Date(g.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </td>
                        <td className="p-4 align-top">
                          <button
                            data-testid={`grv-toggle-${g.id}`}
                            onClick={() => setExpanded(isOpen ? null : g.id)}
                            className="inline-flex items-center gap-1 text-[#F0D788] hover:underline text-xs"
                          >
                            {isOpen ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> View</>}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-white/5 bg-white/[0.02]">
                          <td colSpan={6} className="p-5">
                            <div className="grid md:grid-cols-2 gap-5">
                              <div>
                                <div className="text-[11px] uppercase tracking-wider text-white/55 mb-2">Complaint Details</div>
                                <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap">{g.details}</p>
                              </div>
                              <div>
                                <div className="text-[11px] uppercase tracking-wider text-white/55 mb-2">Customer&apos;s Expected Resolution</div>
                                <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap">
                                  {g.expected_resolution || <span className="text-white/40 italic">— Not provided —</span>}
                                </p>
                                {(g.customer_id || g.branch) && (
                                  <div className="mt-4 text-[11px] text-white/50">
                                    {g.customer_id && <>Customer ID: <span className="text-white/80">{g.customer_id}</span><br /></>}
                                    {g.branch && <>Branch: <span className="text-white/80">{g.branch}</span></>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pagination */}
        <section className="mt-6 flex items-center justify-between text-sm">
          <div className="text-white/55">
            Page <span className="text-white">{page + 1}</span> of {pageCount} · {total} total
          </div>
          <div className="flex gap-2">
            <button
              data-testid="grv-prev"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 hover:border-[#D4AF37]/40 text-xs disabled:opacity-40"
            >
              <ChevronLeft size={13} /> Previous
            </button>
            <button
              data-testid="grv-next"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 hover:border-[#D4AF37]/40 text-xs disabled:opacity-40"
            >
              Next <ChevronRight size={13} />
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
